import { CssBaseline } from '@mui/material';
import { Route, Navigate, createRoutesFromElements, createBrowserRouter } from 'react-router';
import { ApiProvider } from '../apis/sweep';
import Auth0ProviderWithHistory from '../auth/Auth0ProviderWithHistory';
import {
  appRoutes,
  ROUTING_TAB,
  RoutesThatRequireDefaultCreationEnvConnection,
} from '../constants/appRoutes';
import { lazyLoader } from '../lazyLoader';
import { App } from './App';
import { ChatSubscriptionProvider } from './aiAgentCenter/context/ChatSubscriptionContext';
import { ImpersonationMode } from './ImpersonationMode';
import { Dashboard } from './dashboard/Dashboard';
import Home from './pages/Home';
import { Version } from './pages/Version';
import { RedirectToDefaultEnv } from './pages/configuration-canvas/RedirectToDefaultEnv';
import { RuleBuilderDemoPage } from './pages/demos/RuleBuilderDemoPage';
import { HubspotOAuth2Redirect } from './pages/environments/HubspotOAuth2Redirect';
import { SalesforceOAuth2Redirect } from './pages/environments/SalesforceOAuth2Redirect';
import { SlackOAuth2Redirect } from './pages/environments/SlackOAuth2Redirect';
import { UnitedDashboard } from './pages/canvas-pages/united-canvas/UnitedConfigurationCanvas';
import { PanelsProvider } from './panels/PanelsContext';
import noop from 'lodash/noop';
import { ReadOnlyUnitedDashboard } from './pages/canvas-pages/united-canvas/read-only/ReadOnlyUnitedConfigurationCanvas';
import { TeamsOAuth2Redirect } from './pages/environments/TeamsOAuth2Redirect';
import { CookiesProvider } from 'react-cookie';
import { DocumentationPage } from './pages/apps-pages/DocumentationPage';
import { RoutingPageWithProviders } from './pages/apps-pages/RoutingPageWithProviders';
import { MarketingAttributionPanel } from './pages/canvas-pages/canvas-hubspot-page/MarketingAttributionPanel';
import { AutomationPage } from './pages/apps-pages/AutomationPage';
import { AlertsPage } from './pages/apps-pages/AlertsPage';
import { DedupAndMatchingPage } from './pages/apps-pages/DedupAndMatchingPage';
import { createElement } from 'react';

interface RouteRendererProps<T = any> {
  Comp: React.FC<T>;
  withDashboard: boolean;
  propsForComp?: T;
}

function RouteRenderer<T extends {}>({ Comp, withDashboard, propsForComp }: RouteRendererProps<T>) {
  const comp = createElement(Comp, propsForComp);

  return (
    <>
      <CssBaseline />
      <ImpersonationMode />
      {withDashboard ? (
        <PanelsProvider confirmOpen={noop}>
          <Dashboard>{comp}</Dashboard>
        </PanelsProvider>
      ) : (
        comp
      )}
    </>
  );
}

const AppAuth0AndApiProvidersWrapper = () => {
  return (
    <CookiesProvider>
      <Auth0ProviderWithHistory>
        <ApiProvider>
          <ChatSubscriptionProvider>
            <App />
          </ChatSubscriptionProvider>
        </ApiProvider>
      </Auth0ProviderWithHistory>
    </CookiesProvider>
  );
};

/**
 * Handles routes that require default creation environment connection.
 * Generate routes that redirect from routes without env to the same routes with env.
 */
const createRedirectToEnvRoutes = () => {
  const redirectToEnvRoutes: JSX.Element[] = [];

  RoutesThatRequireDefaultCreationEnvConnection.forEach((routePath) => {
    // Find the corresponding app route configuration
    const appRoute = Object.values(appRoutes).find((route) => route.route === routePath);

    if (!appRoute) {
      console.warn(`No appRoute configuration found for ${routePath}`);
      return;
    }

    // Always include the base route (without env)
    const routePatternsWithoutEnv = [routePath];

    // Add any additional subroutes if defined
    if (appRoute.subroutesForRedirectToEnv) {
      appRoute.subroutesForRedirectToEnv.forEach((subroute) => {
        routePatternsWithoutEnv.push(`${routePath}/${subroute}`);
      });
    }

    // Generate redirect routes for each pattern
    routePatternsWithoutEnv.forEach((patternWithoutEnv, index) => {
      redirectToEnvRoutes.push(
        <Route
          key={`redirect-to-env-${routePath}-${index}`}
          path={patternWithoutEnv}
          element={<RouteRenderer withDashboard={true} Comp={RedirectToDefaultEnv} />}
        />,
      );
    });
  });

  return redirectToEnvRoutes;
};

const routes = (
  //official guidelines don't cover integration between auth0 and createBrowserRouter
  //I used solution mentioned in issue linked below
  //https://github.com/remix-run/react-router/discussions/9321
  <Route element={<AppAuth0AndApiProvidersWrapper />}>
    <Route path={appRoutes.salesforce.route} element={<SalesforceOAuth2Redirect />} />
    <Route path={appRoutes.hubspotRedirect.route} element={<HubspotOAuth2Redirect />} />
    <Route path={appRoutes.teamsRedirect.route} element={<TeamsOAuth2Redirect />} />
    <Route path={appRoutes.slack.route} element={<SlackOAuth2Redirect />} />
    <Route path={appRoutes.home.route} element={<Home />}>
      <Route
        path={appRoutes.getStarted.route}
        element={
          <RouteRenderer
            withDashboard={true}
            Comp={lazyLoader(() => import('./pages/get-started/GetStartedEntryPoint'))}
          />
        }
      />
      <Route
        path={appRoutes.funnelMapsPage.route}
        element={
          <RouteRenderer
            withDashboard={true}
            Comp={lazyLoader(() => import('./pages/funnel-maps/FunnelMapsPage'))}
          />
        }
      />
      <Route
        path={`${appRoutes.devopsCenter.route}`}
        element={
          <RouteRenderer
            withDashboard={true}
            Comp={lazyLoader(() => import('./pages/devops-center/DevOpsCenterPage'))}
          />
        }
      />
      <Route
        path={appRoutes.reports.route}
        element={
          <RouteRenderer
            withDashboard={true}
            Comp={lazyLoader(() => import('./pages/reports/ReportsPage'))}
          />
        }
      />
      <Route
        path={appRoutes.sfChangeFeed.route}
        element={
          <RouteRenderer
            withDashboard={true}
            Comp={lazyLoader(() => import('./pages/salesforce-logs/SalesforceLogsPage'))}
          />
        }
      />

      <Route
        path={`${appRoutes.rollups.route}/:crmOrgId`}
        element={
          <RouteRenderer
            withDashboard={true}
            Comp={lazyLoader(() => import('./pages/rollups/RollupsPage'))}
          />
        }
      />
      <Route
        path={appRoutes.aiAssistant.route}
        element={
          <RouteRenderer
            withDashboard={true}
            Comp={lazyLoader(() => import('./pages/ai-assistant/AiAssistantPage'))}
          />
        }
      />

      {/* Demos */}
      <Route
        path={appRoutes.demoRuleBuilder.route}
        element={<RouteRenderer withDashboard={true} Comp={RuleBuilderDemoPage} />}
      />
      <Route
        path={appRoutes.demoComponents.route}
        element={
          <RouteRenderer withDashboard={true} Comp={lazyLoader(() => import('./pages/demos'))} />
        }
      />
      <Route path={appRoutes.version.route} element={<Version />} />
      <Route
        path={`${appRoutes.automations.route}/:crmOrgId`}
        element={<RouteRenderer withDashboard={true} Comp={AutomationPage} />}
      />
      <Route
        path={`${appRoutes.funnelEditor.route}/:funnelMapId`}
        element={<RouteRenderer Comp={UnitedDashboard} withDashboard={false} />}
      />
      <Route
        path={`${appRoutes.funnelEditorReadOnly.route}/:funnelId`}
        element={<RouteRenderer Comp={ReadOnlyUnitedDashboard} withDashboard={false} />}
      />
      <Route
        path={`${appRoutes.documentation.route}/:crmOrgId`}
        element={<RouteRenderer withDashboard={true} Comp={DocumentationPage} />}
      />
      <Route
        path={`${appRoutes.routing.route}/:crmOrgId/${ROUTING_TAB}/:routingTab`}
        element={<RouteRenderer withDashboard={true} Comp={RoutingPageWithProviders} />}
      />
      <Route
        path={`${appRoutes.routing.route}/:crmOrgId`}
        element={<RouteRenderer withDashboard={true} Comp={RoutingPageWithProviders} />}
      />

      <Route
        path={`${appRoutes.alerts.route}/:crmOrgId`}
        element={<RouteRenderer withDashboard={true} Comp={AlertsPage} />}
      />
      <Route
        path={appRoutes.freeTierOnBoarding.route}
        element={
          <RouteRenderer
            withDashboard={false}
            Comp={lazyLoader(() => import('./pages/onboarding/SignupEntryPoint'))}
          />
        }
      />
      <Route
        path={appRoutes.questionnaire.route}
        element={
          <RouteRenderer
            withDashboard={false}
            Comp={lazyLoader(() => import('./pages/questionnaire/QuestionnaireEntryPoint'))}
          />
        }
      />
      <Route
        path={`${appRoutes.shareArtifacts.route}/:versionId`}
        element={
          <RouteRenderer
            withDashboard={false}
            Comp={lazyLoader(() => import('./pages/SharedArtifactPage'))}
          />
        }
      />

      <Route
        path={`${appRoutes.marketingManagement.route}/:crmOrgId`}
        element={<RouteRenderer withDashboard={true} Comp={MarketingAttributionPanel} />}
      />

      <Route
        path={`${appRoutes.dedup.route}/:crmOrgId`}
        element={<RouteRenderer withDashboard={true} Comp={DedupAndMatchingPage} />}
      />
      <Route
        path={`${appRoutes.agenticLayer.route}/:crmOrgId`}
        element={
          <RouteRenderer
            withDashboard={true}
            Comp={lazyLoader(() => import('./aiAgentCenter/AgentsHomePage'))}
          />
        }
      >
        <Route
          index
          element={
            <RouteRenderer
              withDashboard={false}
              Comp={lazyLoader(() => import('./aiAgentCenter/AgentsHomePageView'))}
            />
          }
        />
        <Route
          path="agent/:agentId"
          element={
            <RouteRenderer
              withDashboard={false}
              Comp={lazyLoader(() => import('./aiAgentCenter/AgentsHomePageView'))}
            />
          }
        />
        <Route
          path="agent/:agentId/chat/:chatId"
          element={
            <RouteRenderer
              withDashboard={false}
              Comp={lazyLoader(() => import('./aiAgentCenter/AgentsHomePageView'))}
            />
          }
        />
      </Route>
      <Route
        path={appRoutes.environments.route}
        element={
          <RouteRenderer
            withDashboard={true}
            Comp={lazyLoader(() => import('./pages/environments/page/EnvironmentsPage'))}
          />
        }
      />
      <Route
        path={appRoutes.settings.route}
        element={
          <RouteRenderer
            withDashboard={true}
            Comp={lazyLoader(() => import('./dashboard/user-settings/UserSettings'))}
          />
        }
      />
      {createRedirectToEnvRoutes()}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  </Route>
);

export const router = createBrowserRouter(createRoutesFromElements(routes));
